import Button, { ButtonType } from '../Button';
import styles from './style.module.scss';
import ReactDOM from 'react-dom';
import { ReactNode, useEffect } from 'react';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
export type ModalActions = 'confirm' | 'dismiss' | 'both' | 'none';

export interface ModalButtonProps {
    text: string;
    type?: ButtonType;
    icon?: ReactNode;
    loading?: boolean;
    disabled?: boolean;
    onClick?: () => void;
};

interface Props {
    children: ReactNode;
    title?: string;
    size: ModalSize;
    closeFunc?: any;
    icon?: ReactNode;
    description?: string;
    childClassName?: string;
    /** Which of the footer buttons the modal shows. */
    actions?: ModalActions;
    confirmButton?: ModalButtonProps;
    dismissButton?: ModalButtonProps;
    /** Rendered after the footer buttons, for anything the two of them don't cover. */
    actionsExtra?: ReactNode;
    /** Keeps the footer buttons at their own width instead of splitting the row. */
    restrictActionsWidth?: boolean;
};

function Modal(props: Props) {
    const {
        icon,
        size,
        title,
        children,
        closeFunc,
        description,
        confirmButton,
        dismissButton,
        childClassName,
        actions = 'none',
        actionsExtra,
        restrictActionsWidth,
    } = props;

    const showDismiss = actions === 'dismiss' || actions === 'both';
    const showConfirm = actions === 'confirm' || actions === 'both';
    const hasActions = showDismiss || showConfirm;
    const dom = document.getElementById('modal-root') as HTMLElement;

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        }
    }, []);

    return ReactDOM.createPortal((<div className={styles.backDrop}>
        <div className={styles.container} data-modal-size={size}>
            <div className={styles.header}>
                {icon && <div className={styles.header_icon}>{icon}</div>}
                <div className={styles.header_content}>
                    <div className={styles.header_content_title}>
                        <span className={styles.text}>{title}</span>
                        {closeFunc && <span className={styles.close_icon} onClick={closeFunc}>&times;</span>}
                    </div>
                    {description && <p>{description}</p>}
                </div>
            </div>
            <div
                data-has-actions={hasActions}
                className={`${styles.child} ${childClassName || ''}`}
            >
                {children}
            </div>
            {hasActions && (
                <div className={styles.actions} data-restrict-width={restrictActionsWidth}>
                    {showDismiss && (
                        <Button
                            text={dismissButton?.text ?? 'Cancel'}
                            type={dismissButton?.type ?? 'Outline-Dark'}
                            icon={dismissButton?.icon}
                            onClick={dismissButton?.onClick}
                            loading={dismissButton?.loading}
                            disabled={dismissButton?.disabled}
                            className={styles.actions_btn}
                        />
                    )}
                    {showConfirm && (
                        <Button
                            text={confirmButton?.text ?? 'Confirm'}
                            type={confirmButton?.type ?? 'Primary'}
                            icon={confirmButton?.icon}
                            onClick={confirmButton?.onClick}
                            loading={confirmButton?.loading}
                            disabled={confirmButton?.disabled}
                            className={styles.actions_btn}
                        />
                    )}
                    {actionsExtra}
                </div>
            )}
        </div>
    </div>), dom)
};

export default Modal;
